import React, { useMemo, useState, useEffect } from "react";
import { SubNav, GlassCard } from "../gc/ui";
import SimulatorPanel from "./SimulatorPanel";
import ControlsPanel from "./ControlsPanel";
import { flattenEntities, ttk, ENGINE_TEST_MODES, modeToTestFlags, spriteTexKey } from "../../lib/gamecraft";
import CameraModePicker from "../gc/CameraModePicker";

const BUFFS = [
  { id: "haste", label: "Haste", kind: "buff" },
  { id: "shield", label: "Escudo", kind: "buff" },
  { id: "lifesteal", label: "Robo de vida", kind: "buff" },
  { id: "poison", label: "Veneno", kind: "debuff" },
  { id: "slow", label: "Slow", kind: "debuff" },
  { id: "fragile", label: "Frágil", kind: "debuff" },
];

const DEFAULT_LOOT = [
  { name: "Poción", rate: 40, kind: "potion" },
  { name: "Piedra rúnica", rate: 15, kind: "rune" },
  { name: "Espada", rate: 18, kind: "weapon" },
  { name: "Gema rara", rate: 8, kind: "item" },
  { name: "Nada", rate: 19, kind: "empty" },
];

export default function EngineHub({ project, onChange }) {
  const [sub, setSub] = useState("play");
  const [mode, setMode] = useState("all");
  const [buffs, setBuffs] = useState(["haste"]);
  const [debuffs, setDebuffs] = useState([]);
  const [crt, setCrt] = useState(false);
  const [hitboxes, setHitboxes] = useState(false);
  const [slowMo, setSlowMo] = useState(false);
  const [shake, setShake] = useState(true);
  const [particles, setParticles] = useState(true);
  const [dropRate, setDropRate] = useState(35);
  const [weaponDamage, setWeaponDamage] = useState(12);
  const [enemyCount, setEnemyCount] = useState(3);
  const [floorId, setFloorId] = useState("");
  const [creatureId, setCreatureId] = useState("");
  const [weaponId, setWeaponId] = useState("");
  const [loot, setLoot] = useState(DEFAULT_LOOT);
  const [rolls, setRolls] = useState(null);
  const [lastDrop, setLastDrop] = useState(null);

  const ents = flattenEntities(project);
  const [a, setA] = useState(ents[0]?.id);
  const [b, setB] = useState(ents[1]?.id);
  const attacker = ents.find((e) => e.id === a) || ents[0];
  const defender = ents.find((e) => e.id === b) || ents[1];
  const result = useMemo(() => {
    if (!attacker || !defender) return null;
    return {
      ttk: ttk(defender.hp, attacker.damage, defender.defense),
      ttkRev: ttk(attacker.hp, defender.damage, attacker.defense),
    };
  }, [attacker, defender]);

  const sprites = project.sprites || [];
  const floors = sprites.filter((s) => (s.studio || s.category) === "world" || /tile|terrain|floor|piso/i.test(s.name || ""));
  const creatures = sprites.filter((s) => (s.studio || "creatures") === "creatures");
  const gear = sprites.filter((s) => s.studio === "gear" || /weapon|item|arma/i.test(s.category || s.name || ""));

  useEffect(() => {
    const pt = project.config?.playtest || {};
    if (pt.floorId) setFloorId(pt.floorId);
    if (pt.creatureId) setCreatureId(pt.creatureId);
    if (pt.weaponId) setWeaponId(pt.weaponId);
    if (!floorId && !pt.floorId) {
      const f = floors.find((s) => /piso|Hero|tile|mundo_/i.test(s.name)) || floors[0];
      if (f) setFloorId(f.id);
    }
    if (!creatureId && !pt.creatureId) {
      const c = creatures.find((s) => /slime|enemy/i.test(s.name)) || creatures.find((s) => s.name !== "Hero") || creatures[0];
      if (c) setCreatureId(c.id);
    }
    if (!weaponId && !pt.weaponId) {
      const w = gear.find((s) => /espada|sword/i.test(s.name)) || gear[0];
      if (w) setWeaponId(w.id);
    }
  }, [sprites.length, project.config?.playtest?.floorId, project.config?.playtest?.creatureId, project.config?.playtest?.weaponId]); // eslint-disable-line
  const flags = useMemo(() => modeToTestFlags(mode), [mode]);
  const testConfig = useMemo(() => ({
    ...flags,
    crt,
    hitboxes,
    slowMo,
    shake,
    particles,
    dropRate,
    weaponDamage,
    enemyCount,
    floorTex: spriteTexKey(sprites.find((s) => s.id === floorId)),
    creatureTex: spriteTexKey(sprites.find((s) => s.id === creatureId)),
    weaponTex: spriteTexKey(sprites.find((s) => s.id === weaponId)),
    lootTable: loot,
    buffs,
    debuffs,
    speedMul: debuffs.includes("slow") ? 0.55 : 1,
    dmgTakenMul: debuffs.includes("fragile") ? 1.6 : 1,
  }), [flags, crt, hitboxes, slowMo, shake, particles, dropRate, weaponDamage, enemyCount, floorId, creatureId, weaponId, buffs, debuffs, sprites, loot]);

  const toggleChip = (id, list, setList, exclusive) => {
    setList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : exclusive ? [id] : [...prev, id]));
  };

  const runLoot = (n = 400) => {
    const total = loot.reduce((s, x) => s + Number(x.rate || 0), 0) || 1;
    const counts = Object.fromEntries(loot.map((x) => [x.name, 0]));
    for (let i = 0; i < n; i++) {
      let r = Math.random() * total;
      for (const row of loot) {
        r -= Number(row.rate || 0);
        if (r <= 0) {
          counts[row.name] += 1;
          break;
        }
      }
    }
    setRolls({ n, counts });
  };

  const oneDrop = () => {
    const total = loot.reduce((s, x) => s + Number(x.rate || 0), 0) || 1;
    let r = Math.random() * total;
    let picked = loot[loot.length - 1];
    for (const row of loot) {
      r -= Number(row.rate || 0);
      if (r <= 0) {
        picked = row;
        break;
      }
    }
    setLastDrop(picked.name);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 space-y-3">
        <SubNav
          items={[
            { id: "play", label: "Playtest" },
            { id: "loot", label: "Drops & loot" },
            { id: "stats", label: "Balance TTK" },
            { id: "controls", label: "Controles" },
          ]}
          value={sub}
          onChange={setSub}
        />
        {sub === "play" && (
          <div className="space-y-3">
            <CameraModePicker
              value={project.config?.camera || "side"}
              onChange={(camera) => onChange({ config: { ...(project.config || {}), camera } })}
            />
          <div className="flex flex-wrap gap-2">
            {ENGINE_TEST_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className="px-3 py-2 rounded-2xl text-left border min-w-[120px]"
                style={{
                  borderColor: mode === m.id ? "var(--gc-primary)" : "var(--gc-border)",
                  background: mode === m.id ? "var(--gc-primary-soft)" : "var(--gc-surface)",
                }}
              >
                <div className="text-xs font-semibold">{m.label}</div>
                <div className="text-[10px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{m.hint}</div>
              </button>
            ))}
          </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {sub === "play" && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-4 px-4 pb-4">
            <div className={"gc-tool " + (crt ? "gc-crt" : "")}>
              <SimulatorPanel project={project} onChange={onChange} testConfig={testConfig} />
            </div>
            <aside className="space-y-3">
              <GlassCard className="p-3">
                <div className="text-xs font-semibold mb-2">Qué se prueba</div>
                <div className="text-[11px]" style={{ color: "var(--gc-muted)" }}>
                  {ENGINE_TEST_MODES.find((m) => m.id === mode)?.hint} Pulsa Reset en el simulador para aplicar.
                </div>
                <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                  {Object.entries(flags).map(([k, v]) => (
                    <span key={k} className={v ? "text-emerald-500" : "opacity-40"}>{v ? "●" : "○"} {k}</span>
                  ))}
                </div>
              </GlassCard>

              <GlassCard className="p-3 space-y-2">
                <div className="text-xs font-semibold">Sprites del escenario</div>
                <Field label="Piso / tile">
                  <select className="gc-input h-8 px-2 text-xs w-full" value={floorId} onChange={(e) => setFloorId(e.target.value)}>
                    <option value="">Procedural</option>
                    {floors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label="Criatura">
                  <select className="gc-input h-8 px-2 text-xs w-full" value={creatureId} onChange={(e) => setCreatureId(e.target.value)}>
                    <option value="">Fallback rojo</option>
                    {creatures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label="Arma / artículo">
                  <select className="gc-input h-8 px-2 text-xs w-full" value={weaponId} onChange={(e) => setWeaponId(e.target.value)}>
                    <option value="">Ninguno</option>
                    {gear.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              </GlassCard>

              <GlassCard className="p-3 space-y-2">
                <div className="text-xs font-semibold">Buffs / debuffs</div>
                <div className="flex flex-wrap gap-1">
                  {BUFFS.filter((x) => x.kind === "buff").map((x) => (
                    <button key={x.id} className={"gc-chip " + (buffs.includes(x.id) ? "gc-chip-on" : "")} onClick={() => toggleChip(x.id, buffs, setBuffs)}>
                      {x.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  {BUFFS.filter((x) => x.kind === "debuff").map((x) => (
                    <button key={x.id} className={"gc-chip " + (debuffs.includes(x.id) ? "gc-chip-danger" : "")} onClick={() => toggleChip(x.id, debuffs, setDebuffs)}>
                      {x.label}
                    </button>
                  ))}
                </div>
              </GlassCard>

              <GlassCard className="p-3 space-y-2">
                <div className="text-xs font-semibold">Laboratorio</div>
                <Toggle label="CRT / scanlines" v={crt} set={setCrt} />
                <Toggle label="Hitboxes" v={hitboxes} set={setHitboxes} />
                <Toggle label="Slow-mo" v={slowMo} set={setSlowMo} />
                <Toggle label="Screen shake" v={shake} set={setShake} />
                <Toggle label="Partículas loot" v={particles} set={setParticles} />
                <Field label={`Enemigos ${enemyCount}`}>
                  <input type="range" min="0" max="10" value={enemyCount} onChange={(e) => setEnemyCount(Number(e.target.value))} className="w-full" />
                </Field>
                <Field label={`Daño arma ${weaponDamage}`}>
                  <input type="range" min="2" max="40" value={weaponDamage} onChange={(e) => setWeaponDamage(Number(e.target.value))} className="w-full" />
                </Field>
                <Field label={`Drop rate ${dropRate}%`}>
                  <input type="range" min="1" max="90" value={dropRate} onChange={(e) => setDropRate(Number(e.target.value))} className="w-full" />
                </Field>
              </GlassCard>
            </aside>
          </div>
        )}

        {sub === "loot" && (
          <div className="p-4 max-w-3xl space-y-4">
            <p className="text-sm" style={{ color: "var(--gc-muted)" }}>
              Esta tabla alimenta el spawn del playtest. Ajusta pesos y pulsa Reset en Playtest.
            </p>
            <div className="gc-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase" style={{ color: "var(--gc-muted)" }}>
                    <th className="p-3">Item</th>
                    <th className="p-3">Tipo</th>
                    <th className="p-3">Peso %</th>
                    {rolls && <th className="p-3">Sim {rolls.n}</th>}
                  </tr>
                </thead>
                <tbody>
                  {loot.map((row, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "var(--gc-border)" }}>
                      <td className="p-2">
                        <input className="gc-input h-8 px-2 w-full" value={row.name} onChange={(e) => {
                          const next = [...loot]; next[i] = { ...row, name: e.target.value }; setLoot(next);
                        }} />
                      </td>
                      <td className="p-2">
                        <select className="gc-input h-8 px-2 w-full text-xs" value={row.kind || "item"} onChange={(e) => {
                          const next = [...loot]; next[i] = { ...row, kind: e.target.value }; setLoot(next);
                        }}>
                          <option value="potion">Poción</option>
                          <option value="weapon">Arma</option>
                          <option value="rune">Piedra rúnica</option>
                          <option value="item">Objeto</option>
                          <option value="empty">Nada</option>
                        </select>
                      </td>
                      <td className="p-2">
                        <input type="number" className="gc-input h-8 px-2 w-24" value={row.rate} onChange={(e) => {
                          const next = [...loot]; next[i] = { ...row, rate: Number(e.target.value) }; setLoot(next);
                        }} />
                      </td>
                      {rolls && (
                        <td className="p-2">
                          <div className="gc-bar">
                            <span style={{ width: `${(rolls.counts[row.name] / rolls.n) * 100}%` }} />
                          </div>
                          <span className="text-[11px]">{rolls.counts[row.name]} ({Math.round((rolls.counts[row.name] / rolls.n) * 100)}%)</span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <button className="gc-btn gc-btn-primary h-10 px-4" onClick={() => runLoot(400)}>Simular 400 drops</button>
              <button className="gc-btn gc-btn-ghost h-10 px-4" onClick={oneDrop}>Abrir cofre</button>
              <button className="gc-btn gc-btn-ghost h-10 px-4" onClick={() => setLoot([...loot, { name: "Nuevo", rate: 5 }])}>+ fila</button>
              {lastDrop && <span className="gc-loot-pop">✦ {lastDrop}</span>}
            </div>
          </div>
        )}

        {sub === "controls" && <ControlsPanel project={project} onChange={onChange} />}

        {sub === "stats" && (
          <div className="p-4 max-w-3xl space-y-4">
            <p className="text-sm" style={{ color: "var(--gc-muted)" }}>
              Compara entidades (armas, mobs, bosses) para estimar tiempo de muerte (TTK) usando HP, daño y defensa.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select className="gc-input h-10 px-3" value={a || ""} onChange={(e) => setA(e.target.value)}>
                {ents.map((e) => <option key={e.id} value={e.id}>{e.kind}: {e.name}</option>)}
              </select>
              <select className="gc-input h-10 px-3" value={b || ""} onChange={(e) => setB(e.target.value)}>
                {ents.map((e) => <option key={e.id} value={e.id}>{e.kind}: {e.name}</option>)}
              </select>
            </div>
            {result && attacker && defender && (
              <div className="gc-card p-5 grid grid-cols-2 gap-4">
                <Stat label={`${attacker.name} vs ${defender.name}`} value={`${result.ttk}s TTK`} />
                <Stat label={`${defender.name} vs ${attacker.name}`} value={`${result.ttkRev}s TTK`} />
                <Stat label="HP A / B" value={`${attacker.hp} / ${defender.hp}`} />
                <Stat label="DMG A / B" value={`${attacker.damage} / ${defender.damage}`} />
              </div>
            )}
            {ents.length < 2 && <p className="text-sm">Crea al menos dos ideas (jugador y enemigo) con stats.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-[11px]" style={{ color: "var(--gc-muted)" }}>
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Toggle({ label, v, set }) {
  return (
    <label className="flex items-center justify-between text-xs">
      {label}
      <input type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} />
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-semibold" style={{ color: "var(--gc-muted)" }}>{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}
